/**
 * The predictive Back gesture, from the shell's own `BackGesture` plugin
 * (`android/app/src/main/java/app/mypocketgym/dev/BackGesturePlugin.java`,
 * `D193`).
 *
 * `@capacitor/app` forwards only the END of a Back — `backButton`, once the
 * finger has let go. Android 14+ reports the whole gesture to an app that
 * opts in (`android:enableOnBackInvokedCallback`), and this plugin passes it
 * on: started, progressed (0→1, with the edge it came from), cancelled, or
 * invoked. The app then moves its top layer with the finger and commits or
 * springs back, as a native app does.
 *
 * THE PLUGIN'S CALLBACK STARTS DISABLED, and only this file enables it. An
 * older bundle running in this shell knows nothing of it, so the shell must
 * keep answering Back through `@capacitor/app` until a bundle that listens
 * here says otherwise — enabled from birth, it would swallow every Back into
 * an event nobody hears. The reverse holds too: a new bundle in an OLD shell
 * finds no plugin (`backGestureAvailable()` is false) and keeps the
 * `backButton` path.
 *
 * Web: unavailable, every function a no-op.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { isNative, toUnsubscribe, type Unsubscribe } from "./platform";

export type BackEdge = "left" | "none" | "right";

export interface BackProgress {
  /** Where the swipe started: the screen's left or right edge, or none (a button). */
  readonly edge: BackEdge;
  /** 0 at the start of the gesture, 1 at its furthest. */
  readonly progress: number;
}

interface RawBackEvent {
  readonly progress?: number;
  readonly swipeEdge?: number;
}

interface BackGesturePlugin {
  addListener(
    event: "backStarted" | "backProgressed",
    handler: (event: RawBackEvent) => void
  ): Promise<{ remove: () => Promise<void> }>;
  addListener(
    event: "backCancelled" | "backInvoked",
    handler: () => void
  ): Promise<{ remove: () => Promise<void> }>;
  setEnabled(options: { enabled: boolean }): Promise<void>;
}

const PLUGIN = "BackGesture";

let plugin: BackGesturePlugin | null = null;

function backGesture(): BackGesturePlugin {
  plugin ??= registerPlugin<BackGesturePlugin>(PLUGIN);
  return plugin;
}

/** Whether this shell carries the plugin — false on the web and in older shells. */
export function backGestureAvailable(): boolean {
  return isNative() && Capacitor.isPluginAvailable(PLUGIN);
}

/** AndroidX's `BackEventCompat.EDGE_*`: 0 left, 1 right, 2 none. */
export function edgeOf(swipeEdge: number | undefined): BackEdge {
  if (swipeEdge === 0) {
    return "left";
  }
  if (swipeEdge === 1) {
    return "right";
  }
  return "none";
}

function toProgress(event: RawBackEvent): BackProgress {
  const progress = Math.min(1, Math.max(0, event.progress ?? 0));
  return { edge: edgeOf(event.swipeEdge), progress };
}

export interface BackGestureHandlers {
  readonly onCancelled: () => void;
  readonly onInvoked: () => void;
  readonly onProgressed: (event: BackProgress) => void;
  readonly onStarted: (event: BackProgress) => void;
}

/** Listens to the gesture. Unavailable: a no-op. */
export function onBackGesture(handlers: BackGestureHandlers): Unsubscribe {
  if (!backGestureAvailable()) {
    return () => undefined;
  }
  const native = backGesture();
  const stops = [
    toUnsubscribe(
      native.addListener("backStarted", (event) =>
        handlers.onStarted(toProgress(event))
      )
    ),
    toUnsubscribe(
      native.addListener("backProgressed", (event) =>
        handlers.onProgressed(toProgress(event))
      )
    ),
    toUnsubscribe(native.addListener("backCancelled", handlers.onCancelled)),
    toUnsubscribe(native.addListener("backInvoked", handlers.onInvoked)),
  ];
  return () => {
    for (const stop of stops) {
      stop();
    }
  };
}

/**
 * Whether the app takes Back at all. `false` hands it to the system, which
 * plays its own back-to-home animation and sends the app to the background —
 * what Back should do at Today with nothing open.
 */
export async function setBackGestureEnabled(enabled: boolean): Promise<void> {
  if (!backGestureAvailable()) {
    return;
  }
  await backGesture().setEnabled({ enabled });
}

/**
 * Turns `@capacitor/app`'s own Back callback off, once this plugin owns Back.
 * Left on, it would take the press whenever this plugin hands Back to the
 * system, and the back-to-home animation would never play.
 */
export async function releaseAppBackHandler(): Promise<void> {
  if (!backGestureAvailable()) {
    return;
  }
  const { App } = await import("@capacitor/app");
  await App.toggleBackButtonHandler({ enabled: false });
}
